/** Polygon list/card identity: KIA as primary title, full construction name as subtitle. */

import type { FilterMultiOption } from './filter-multi.util';

export interface PolygonDisplayInput {
  id?: string | null;
  kiaCode?: string | null;
  name?: string | null;
  folderNumber?: string | null;
}

/** Primary label — KIA when set, otherwise full name, otherwise id. */
export function getPolygonDisplayTitle(polygon: PolygonDisplayInput | null | undefined): string {
  if (!polygon) return '';
  const kia = (polygon.kiaCode ?? '').trim();
  if (kia) return kia;
  return (polygon.name ?? '').trim() || (polygon.id ?? '').trim();
}

/** Secondary line — full name when KIA is the primary title and name differs. */
export function getPolygonDisplaySubtitle(polygon: PolygonDisplayInput | null | undefined): string {
  if (!polygon) return '';
  const kia = (polygon.kiaCode ?? '').trim();
  const name = (polygon.name ?? '').trim();
  if (!kia || !name || name === kia) return '';
  return name;
}

/**
 * Single-line label for non-filter UI (import pickers, confirms, search history).
 * KIA first; when name differs: `KIA – Název`. Falls back to name, then id.
 */
export function getPolygonDisplayLabel(polygon: PolygonDisplayInput | null | undefined): string {
  if (!polygon) return '';
  const title = getPolygonDisplayTitle(polygon);
  const subtitle = getPolygonDisplaySubtitle(polygon);
  if (title && subtitle) return `${title} – ${subtitle}`;
  return title;
}

/**
 * Label for polygon multi-select filters — KIA as primary (name goes in `subtitle`).
 * Same fallbacks as {@link getPolygonDisplayTitle}.
 */
export function getPolygonFilterLabel(polygon: PolygonDisplayInput | null | undefined): string {
  return getPolygonDisplayTitle(polygon);
}

/** One option for `app-filter-multi-select` polygon filters — KIA + full-name subtitle. */
export function polygonToFilterOption(
  polygon: PolygonDisplayInput & { id: string },
): FilterMultiOption {
  const label = getPolygonFilterLabel(polygon);
  const subtitle = getPolygonDisplaySubtitle(polygon);
  const searchText = polygonFilterSearchText(polygon, label);
  return {
    value: polygon.id,
    label,
    ...(subtitle ? { subtitle } : {}),
    ...(searchText ? { searchText } : {}),
  };
}

/** Name and folder stay searchable when the checkbox shows only the KIA. */
function polygonFilterSearchText(polygon: PolygonDisplayInput, label: string): string | undefined {
  const extra = [polygon.name, polygon.folderNumber]
    .map((part) => String(part ?? '').trim())
    .filter((part) => part && part !== label);
  if (!extra.length) return undefined;
  return [label, ...extra].join(' ');
}

/** Build sorted polygon filter options (KIA + name subtitle) for multi-select. */
export function toPolygonFilterOptions(
  polygons: ReadonlyArray<PolygonDisplayInput & { id: string }>,
): FilterMultiOption[] {
  return [...polygons]
    .map(polygonToFilterOption)
    .sort((a, b) => a.label.localeCompare(b.label, 'cs'));
}
